import React, { FC, MouseEvent, useState } from 'react'
/** @jsx jsx */
import { jsx, css, SerializedStyles } from '@emotion/core'
import Analytics from '@lib/Analytics'
import Pixels from '@lib/Pixels'
import { getPhoneNumber } from '@lib/PhoneNumber'
import { PhoneNumber } from '@components/PhoneNumber'
import { Button } from '@components/Button'
import { PhoneRingingIcon, ChevronRightIcon } from '@components/Icons'
import { MiniPopup } from '@components/Popup'
import { DesktopOnly, MobileOnly } from '@components/MediaQuery'
import {
  RequestCallWrapper,
  PhoneNumberWrapper,
  SpeakText,
  PopupSubtitle,
  PhoneRingingIconWrapper,
  MobilePhoneIconWrapper,
  callButtonStyles,
  getQuoteButtonStyles,
  popupCallButtonStyles,
  popupRequestCallButtonStyles, SpeakTo,
} from './RequestCall.elements'

type Props = {
  phoneMapping: PhoneMapping
  greenBtnText?: string
  onGreenBtnCklick?: () => void
  onRequestCall: () => void
  headerText?: string
}

export const RequestCall: FC<Props> = ({ children, ...props }) => {
  const {
    greenBtnText = 'Call now',
    onGreenBtnCklick,
    phoneMapping,
    onRequestCall,
    headerText
  } = props

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false)

  const handleGreenBtnClick = () => {
    if (onGreenBtnCklick) {
      Analytics.log('Click', { location: 'header', type: 'quote' })
      onGreenBtnCklick()
    } else {
      handleOpenPopup()
    }
  }

  const handleClosePopup = () => {
    setIsPopupOpen(false)
  }

  const handleOpenPopup = () => {
    Analytics.log('Click', { location: 'header', type: 'call' })
    setIsPopupOpen(true)
  }

  const handleRequestCall = () => {
    onRequestCall()
    handleClosePopup()
  }

  const handleCall = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const number = getPhoneNumber(phoneMapping)

    Pixels.callEvent()
    Analytics.log('Click To Call', {
      location: 'header', info: `tel:${ number }`
    })

    window.location.href = `tel:${ number }`
  }

  return (
    <RequestCallWrapper>
      <DesktopOnly>
        <PhoneNumberWrapper>
          <SpeakTo headerText={headerText}>
            <Button
              variant="link"
              onClick={handleCall}
              aria-label="Speak to a licensed insurance agent"
              css={callButtonStyles}
            >
              <PhoneNumber
                mapping={phoneMapping}
                format="pretty"
              />
            </Button>

            <div>
              <SpeakText dangerouslySetInnerHTML={{ __html: "Speak to a licensed insurance&nbsp;agent" }} />
              <SpeakText>8am - 8pm EST (Mon-Fri)</SpeakText>
            </div>
          </SpeakTo>

          {onGreenBtnCklick && (
            <Button
              variant="green"
              onClick={handleGreenBtnClick}
              css={getQuoteButtonStyles}
            >
              {greenBtnText}
            </Button>
          )}
        </PhoneNumberWrapper>
      </DesktopOnly>

      <MobileOnly>
        <Button
          variant="text"
          circled
          onClick={handleOpenPopup}
          aria-label="Speak to a licensed insurance agent"
          fixedFont
        >
          <MobilePhoneIconWrapper>
            <PhoneRingingIcon />
          </MobilePhoneIconWrapper>
        </Button>
      </MobileOnly>

      {isPopupOpen && renderPopup({
        handleClosePopup,
        handleCall,
        phoneMapping,
        handleRequestCall,
      })}
    </RequestCallWrapper>
  )
}

function renderPopup({
  handleClosePopup,
  handleCall,
  phoneMapping,
  handleRequestCall,
}) {
  return (
    <MiniPopup
      maxWidth={343}
      onClose={handleClosePopup}
      title="Speak to a licensed insurance&nbsp;agent"
    >
      <PopupSubtitle>8am - 8pm EST (Mon-Fri)</PopupSubtitle>
      <Button
        variant="green"
        onClick={handleCall}
        aria-label="Speak to a licensed insurance agent"
        css={popupCallButtonStyles}
      >
        <PhoneRingingIconWrapper>
          <PhoneRingingIcon />
        </PhoneRingingIconWrapper>
        <PhoneNumber
          mapping={phoneMapping}
          format="pretty"
        />
      </Button>

      <Button
        variant="text"
        onClick={handleRequestCall}
        css={popupRequestCallButtonStyles}
      >
        REQUEST A CALL
        <ChevronRightIcon />
      </Button>
    </MiniPopup>
  )
}
